<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { Illustration } from '../Illustration';
  import {
    cardSurfaceClass,
    containerClass,
    contentClass,
    descriptionClass,
    fullWidthClass,
    illustrationClass,
    plainSurfaceClass,
    textBlockClass,
    titleClass,
  } from './EmptyState.css';
  import { EmptyStateSurface } from './EmptyState.types';
  import type { EmptyStateProps } from './EmptyState.types';

  const props = withDefaults(defineProps<EmptyStateProps>(), {
    svgWidth: 130,
    svgHeight: 130,
    surface: EmptyStateSurface.CARD,
    fullWidth: false,
    titleAs: 'h2',
  });

  const slots = defineSlots<{
    illustration?: () => unknown;
    title?: () => unknown;
    description?: () => unknown;
    action?: () => unknown;
  }>();

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-empty-state-${uid}`,
  );

  const containerClasses = computed(() => [
    containerClass,
    props.surface === EmptyStateSurface.CARD
      ? cardSurfaceClass
      : plainSurfaceClass,
    props.fullWidth && fullWidthClass,
  ]);

  const hasAction = computed(() => !!slots.action);
</script>

<template>
  <div :id="id" :class="containerClasses" :data-testid="resolvedTestId">
    <div :class="illustrationClass" aria-hidden="true">
      <slot name="illustration">
        <Illustration
          v-if="svgImage"
          :src="svgImage"
          :alt="svgTitle"
          :width="svgWidth"
          :height="svgHeight"
        />
      </slot>
    </div>

    <div :class="contentClass">
      <div :class="textBlockClass">
        <component
          :is="titleAs"
          :class="titleClass"
          :data-testid="`${resolvedTestId}-title`"
        >
          <slot name="title">{{ title }}</slot>
        </component>
        <slot name="description">
          <p
            v-if="description"
            :class="descriptionClass"
            :data-testid="`${resolvedTestId}-description`"
          >
            {{ description }}
          </p>
        </slot>
      </div>

      <div v-if="hasAction" :data-testid="`${resolvedTestId}-action`">
        <slot name="action" />
      </div>
    </div>
  </div>
</template>
